import {
    any,
    array,
    boolean,
    Describe,
    enums,
    literal,
    nullable,
    number,
    object,
    optional,
    string,
    union,
} from "superstruct";
import { BillingMode } from "./BillingMode";
import { Customer } from "./Customer";
import { OperatingUnit } from "./OperatingUnit";
import { IdentifiablePhoto, Order } from "./Order";
import { Role } from "./Role";
import { AnyTimeTrackingMapStructure, TimeTrackingMapStructureType } from "./TimeTracking";
import { getInternalWorkTypeName, InternalWorkType } from "./workTypes/InternalWorkType";
import { getOrderWorkTypeName, OrderWorkType } from "./workTypes/OrderWorkType";

export enum EmployeeStatus {
    INVITED = "invited",
    ACTIVE = "active",
    REJECTED = "rejected",
    LEFT = "left",
    NEW = "new",
}

export enum ActivityType {
    ORDER = "ORDER",
    INTERNAL = "INTERNAL",
}

// Superstruct does only support record of type Record<string, T> and not Record<number, T>
export const EmployeeStruct: Describe<Omit<Employee, "yearlyVacationDays">> = object({
    active: boolean(),
    appUserId: nullable(string()),
    photo: nullable(object({ storagePath: string(), imageSrc: string() })),
    archived: boolean(),
    costPerHour: number(),
    currentActivity: nullable(
        union([
            object({
                type: literal(ActivityType.INTERNAL),
                internalWorkType: enums(Object.values(InternalWorkType)),
                startDateTime: string(),
                note: optional(string()),
            }),
            object({
                type: literal(ActivityType.ORDER),
                orderId: string(),
                orderWorkType: enums(Object.values(OrderWorkType)),
                customerId: nullable(string()),
                startDateTime: string(),
                note: optional(string()),
                mapStructure: nullable(
                    object({
                        type: enums(Object.values(TimeTrackingMapStructureType)),
                        fieldId: string(),
                    })
                ),
            }),
        ])
    ),
    currentMonthBillingMode: enums(Object.values(BillingMode)),
    firstName: string(),
    id: string(),
    invitationTarget: nullable(string()),
    lastName: string(),
    phone: string(),
    nextMonthBillingMode: enums(Object.values(BillingMode)),
    other: string(),
    role: enums(Object.values(Role)),
    status: enums(Object.values(EmployeeStatus)),
    targetHoursMonthly: number(),
    targetWorkHoursWeekly: nullable(number()),
    yearlyVacationDays: any(),
    operatingUnitIds: array(string()),
    hiringDate: nullable(string()),
    color: string(),
});

export class Employee {
    public active: boolean;
    public appUserId: string | null;
    public photo: IdentifiablePhoto | null;
    public archived: boolean;
    public costPerHour: number;
    public currentActivity: Activity | null;
    public currentMonthBillingMode: BillingMode;
    public firstName: string;
    public id: string;
    public invitationTarget: string | null;
    public lastName: string;
    public phone: string;
    public nextMonthBillingMode: BillingMode;
    public other: string;
    public role: Role;
    public status: EmployeeStatus;
    /**
     * @deprecated
     */
    public targetHoursMonthly: number;
    public targetWorkHoursWeekly: number | null;
    public yearlyVacationDays: { [year: number]: number | null };
    public operatingUnitIds: Array<OperatingUnit["id"]>;
    /** Date as ISO-String */
    public hiringDate: string | null;
    /** Color as Hex string*/
    public color: string;

    constructor(initialValues?: Partial<Employee>) {
        this.active = initialValues?.active ?? true;
        this.archived = initialValues?.archived ?? false;
        this.appUserId = initialValues?.appUserId ?? null;
        this.photo = initialValues?.photo?.imageSrc && initialValues?.photo.storagePath ? initialValues.photo : null;
        this.costPerHour = initialValues?.costPerHour ?? 0;
        this.currentActivity = initialValues?.currentActivity ?? null;
        this.currentMonthBillingMode = initialValues?.currentMonthBillingMode ?? BillingMode.PER_MONTH;
        this.firstName = initialValues?.firstName ?? "";
        this.id = initialValues?.id ?? "";
        this.invitationTarget = initialValues?.invitationTarget ?? null;
        this.lastName = initialValues?.lastName ?? "";
        this.nextMonthBillingMode = initialValues?.nextMonthBillingMode ?? this.currentMonthBillingMode;
        this.other = initialValues?.other ?? "";
        this.role = initialValues?.role ?? Role.TEMPORARY_WORKER;
        this.yearlyVacationDays = initialValues?.yearlyVacationDays ?? {};
        this.operatingUnitIds = initialValues?.operatingUnitIds ?? [];
        this.status = initialValues?.status ?? EmployeeStatus.NEW;
        this.targetHoursMonthly = initialValues?.targetHoursMonthly ?? 0;
        this.targetWorkHoursWeekly = initialValues?.targetWorkHoursWeekly ?? null;
        this.hiringDate = initialValues?.hiringDate ?? null;
        this.phone = initialValues?.phone ?? "";
        this.color = initialValues?.color ?? "#ffce00";
    }
}

export function getActivityWorkTypeName(activity: Activity): string {
    if (activity.type === ActivityType.INTERNAL) {
        return getInternalWorkTypeName(activity.internalWorkType);
    } else if (activity.type === ActivityType.ORDER) {
        return getOrderWorkTypeName(activity.orderWorkType);
    }

    return "Keine Aktivität ausgewählt";
}

export interface OrderActivity {
    type: ActivityType.ORDER;
    orderId: Order["id"];
    orderWorkType: OrderWorkType;
    customerId: Customer["id"] | null;
    startDateTime: string; // ISO-Datetime
    note?: string;
    mapStructure: AnyTimeTrackingMapStructure | null;
}

export interface InternalActivity {
    type: ActivityType.INTERNAL;
    internalWorkType: InternalWorkType;
    startDateTime: string; // ISO-Datetime
    note?: string;
}

export type Activity = OrderActivity | InternalActivity;
